import { useEffect, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import sfLogo from "@/assets/startupfuel-logo.png";
import { cn } from "@/lib/utils";
import { ecosystemMapUrl } from "@/lib/ecosystem-taxonomy";

/*
 * Shared shell + primitives for the public ecosystem pages.
 *
 * These run the `ecosystem-theme` skin defined in index.css, which is the
 * startupfuel.com map palette: warm paper, near-black ink, hairline rules,
 * navy actions, square corners. Presentation only — no data fetching.
 *
 * The chrome is deliberately a copy of the marketing site's rather than the
 * app's: a visitor lands here from /ecosystem/canada, and a header swap
 * mid-journey reads as "wrong site" long before it reads as "same company".
 */

const SF_HOME = "https://www.startupfuel.com";

/** Page wrapper: applies the skin and owns the scroll container. */
export function EcoShell({ children }: { children: ReactNode }) {
  // The app shell locks body scroll; public pages scroll their own root.
  useEffect(() => {
    document.body.classList.add("public-route");
    return () => document.body.classList.remove("public-route");
  }, []);

  return (
    <div className="ecosystem-theme flex h-dvh flex-col overflow-y-auto bg-background font-sans text-foreground">
      {children}
    </div>
  );
}

/** Centred column. 1240px is the marketing site's `.pf-shell` measure. */
export function EcoContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mx-auto w-full max-w-[1240px] px-4 sm:px-6", className)}>
      {children}
    </div>
  );
}

export function EcoHeader({ ecosystem }: { ecosystem: string }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
      <EcoContainer className="flex h-[68px] items-center justify-between gap-4">
        {/* 176px is the wordmark's width in the site header it's copied from.
            The asset is vendored rather than hotlinked so the page doesn't
            depend on the marketing site being up. */}
        <a
          href={SF_HOME}
          className="block w-[140px] shrink-0 transition-opacity hover:opacity-80 sm:w-[176px]"
          aria-label="StartupFuel home"
        >
          <img src={sfLogo} alt="StartupFuel" className="h-auto w-full" />
        </a>
        <nav
          className="flex items-center gap-6 text-[11px] font-semibold uppercase tracking-[0.12em]"
          aria-label="Ecosystem navigation"
        >
          <a
            href={ecosystemMapUrl(ecosystem)}
            className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline"
          >
            The map
          </a>
          <a
            href={`${SF_HOME}/contact`}
            className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline"
          >
            Contact
          </a>
          <EcoButton as="a" href={ecosystemMapUrl(ecosystem)} variant="secondary">
            Back to the map
          </EcoButton>
        </nav>
      </EcoContainer>
    </header>
  );
}

export function EcoFooter() {
  return (
    <footer className="mt-auto border-t border-border bg-background py-10">
      <EcoContainer className="space-y-4">
        {/* Not inverted, unlike the source site's footer — that one sits on a
            dark band, this one on the same paper as the page. */}
        <img
          src={sfLogo}
          alt="StartupFuel"
          loading="lazy"
          className="h-auto w-[180px]"
        />
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          StartupFuel provides software, structured information, analytical
          tools, and ecosystem infrastructure. Information displayed through
          StartupFuel does not constitute investment advice, an investment
          recommendation, verification, solicitation, or a guarantee of funding
          or performance.
        </p>
        <p className="text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} StartupFuel. All rights reserved.
        </p>
      </EcoContainer>
    </footer>
  );
}

/**
 * The small red all-caps label above a display heading. It is the map's one
 * accent colour and marks the start of a section.
 */
export function EcoEyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-destructive",
        className
      )}
    >
      {children}
    </span>
  );
}

/** Breadcrumb trail, matching `.eco-crumbs`. */
export function EcoCrumbs({
  items,
}: {
  items: Array<{ label: string; href?: string }>;
}) {
  return (
    <nav
      className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground"
      aria-label="Breadcrumb"
    >
      {items.map((item, i) => (
        <span key={item.label} className="flex items-center gap-2">
          {i > 0 && <ChevronRight className="h-3 w-3" aria-hidden="true" />}
          {item.href ? (
            <a href={item.href} className="transition-colors hover:text-foreground">
              {item.label}
            </a>
          ) : (
            <span aria-current="page">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/**
 * The map's pill button — uppercase, tracked, navy fill or ink outline.
 *
 * Rendered as `<a>` or `<button>` from one definition because the same
 * treatment is used for both on the source pages, and splitting it would let
 * the two drift.
 */
type EcoButtonProps = {
  variant?: "primary" | "secondary";
  className?: string;
  children: ReactNode;
} & (
  | ({ as?: "button" } & React.ButtonHTMLAttributes<HTMLButtonElement>)
  | ({ as: "a" } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
);

export function EcoButton({
  variant = "primary",
  className,
  children,
  ...props
}: EcoButtonProps) {
  const classes = cn(
    "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border px-5 text-[11px] font-bold uppercase tracking-[0.1em] transition-all",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    variant === "primary"
      ? "border-primary bg-primary text-primary-foreground hover:-translate-y-0.5 hover:border-foreground hover:bg-foreground"
      : "border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background",
    className
  );

  const { as = "button", ...rest } = props as {
    as?: "a" | "button";
  } & Record<string, unknown>;

  if (as === "a") {
    return (
      <a
        className={classes}
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    );
  }

  const { type = "button", ...buttonProps } =
    rest as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={type} className={classes} {...buttonProps}>
      {children}
    </button>
  );
}

/** Bordered aside panel — `.eco-aside-card`. */
export function EcoAsideCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="border border-border bg-background p-5">
      <small className="block text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        {title}
      </small>
      <div className="mt-2.5 text-[13px] leading-relaxed">{children}</div>
    </div>
  );
}

/** Small-caps heading that opens a group of fields. */
export function EcoSectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      {children}
    </h2>
  );
}

/** The map's metadata pill — `.eco-badge`. */
export function EcoBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-border px-2 py-[3px] text-[10px] text-muted-foreground">
      {children}
    </span>
  );
}
